import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { isEqual } from 'lodash' // Adicione lodash para comparação
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useFetchUserById } from '@/features/users/services'
import { TypeCargo, TypeCargoAdmin, TypeCargoUser, TypeUsers } from '@/lib/enums'

const userFormSchema = z.object({
  name: z.string().optional(),
  email: z.string().email('Invalid email address').optional(),
  cpf: z.string().optional(),
  tel: z.string().optional(),
  whatsApp: z.string().optional(),
  typeUser: z.nativeEnum(TypeUsers).optional(),
  typeCargo: z.union([z.nativeEnum(TypeCargo), z.nativeEnum(TypeCargoAdmin), z.nativeEnum(TypeCargoUser)]).optional(),
  cnpj: z.string().optional(),
  razao_social: z.string().optional(),
  cep: z.string().optional(),
  logradouro: z.string().optional(),
  bairro: z.string().optional(),
  numero: z.string().optional(),
  municipio: z.string().optional(),
  estado: z.string().optional()
})

type UserFormValues = z.infer<typeof userFormSchema>

export default function UserEditForm({ id_user }: { id_user: string }) {
  const [originalData, setOriginalData] = useState<UserFormValues | null>(null)
  const [hasChanges, setHasChanges] = useState(false) // Estado para controlar mudanças
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const fetchUserByID = useFetchUserById()

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {} as UserFormValues // Enforce explicit type here
  })

  // Load user data on mount
  useEffect(() => {
    async function loadUserData() {
      try {
        const user = await fetchUserByID.mutateAsync(id_user)
        setOriginalData(user.data) // Armazena os dados originais no estado
        form.reset(user) // Atualiza os valores iniciais do formulário
      } catch (error) {
        console.error('Error fetching user:', (error as Error).message)
      }
    }
    loadUserData()
  }, [id_user, fetchUserByID, form])

  // Watch for changes in form data
  useEffect(() => {
    const subscription = form.watch(currentValues => {
      if (originalData) {
        setHasChanges(!isEqual(currentValues, originalData))
      }
    })
    return () => subscription.unsubscribe()
  }, [form, originalData])

  async function onSubmit(data: UserFormValues) {
    setIsLoading(true)

    if (hasChanges) {
      console.log('Submitting changes:', data)
      // Envie os dados atualizados para a API
    } else {
      console.log('No changes to submit.')
    }

    setIsLoading(false)
  }

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>Edit User</CardTitle>
        <CardDescription>Make changes to the user profile here.</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cpf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telephone</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="whatsApp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="typeUser"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>User Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um user type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(TypeUsers).map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="typeCargo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cargo Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select cargo type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {/* Garantimos que os valores sejam únicos usando Set */}
                      {Array.from(
                        new Set([
                          ...Object.values(TypeCargo),
                          ...Object.values(TypeCargoAdmin),
                          ...Object.values(TypeCargoUser)
                        ])
                      ).map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cnpj"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CNPJ</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="razao_social"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Razão Social</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cep"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CEP</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="logradouro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logradouro</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bairro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bairro</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="numero"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="municipio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Município</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="estado"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estado</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}
